import {
  useId,
  useRef,
  useState,
  useCallback,
  type ElementType,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type CSSProperties,
  type RefCallback,
} from 'react';

import { useTooltipBounds } from '../contexts';
import { cn } from '../cn';
import { composeVariant, tooltipClassName, tooltipStyle } from './buttonClasses';
import type { ColorName, ColorVariant, Style, TooltipPosition } from './buttonClasses';

/**
 * Show/hide a native popover defensively. `showPopover()` throws an `InvalidStateError` when the popover
 * is already open OR when called mid-transition of another popover ("Invalid to show a popover during
 * another show operation") — which happens when focus jumps between tooltip-wrapped elements while a
 * popover (e.g. a confirm-delete) is closing, as when a step is removed. `hidePopover()` throws when the
 * popover isn't open. A tooltip is non-essential, so swallow those state errors rather than crash the app.
 */
function safeTogglePopover(el: HTMLElement | null, action: 'show' | 'hide'): void {
  if (el == null) return;
  try {
    if (action === 'show') el.showPopover();
    else el.hidePopover();
  } catch {
    // Popover already in the target state or another popover is mid-transition — nothing to do.
  }
}

/** Get tooltip position - uses explicit position if provided, otherwise defaults to 'top' */
function getSmartPosition(
  _anchorElement: Element | null,
  _containerLeftX: number | undefined,
  explicitPosition: TooltipPosition | undefined,
): TooltipPosition {
  return explicitPosition ?? 'top';
}

// ============ HOOK ============
type TooltipHandlers<E = Element> = {
  onMouseEnter?: React.MouseEventHandler<E>;
  onMouseLeave?: React.MouseEventHandler<E>;
  onFocus?: React.FocusEventHandler<E>;
  onBlur?: React.FocusEventHandler<E>;
};

/** Hook for tooltip with popover API. Returns anchorName, style, ref, and merged hover handlers. */
export function useTooltipAnchor<E = Element>(
  tooltip: ReactNode | undefined,
  handlers?: TooltipHandlers<E>,
) {
  const id = useId();
  const popoverRef = useRef<HTMLSpanElement>(null);
  const [anchorElement, setAnchorElement] = useState<Element | null>(null);
  const anchorName = tooltip ? `--tip-${id.replace(/:/g, '')}` : undefined;
  const anchorStyle = anchorName ? ({ anchorName } as CSSProperties) : undefined;

  // RefCallback to track the anchor element
  const anchorRef = useCallback<RefCallback<E>>((node) => {
    setAnchorElement(node as Element | null);
  }, []);

  const hoverHandlers: TooltipHandlers<E> = tooltip
    ? {
        onMouseEnter: (e) => {
          safeTogglePopover(popoverRef.current, 'show');
          handlers?.onMouseEnter?.(e);
        },
        onMouseLeave: (e) => {
          safeTogglePopover(popoverRef.current, 'hide');
          handlers?.onMouseLeave?.(e);
        },
        onFocus: (e) => {
          safeTogglePopover(popoverRef.current, 'show');
          handlers?.onFocus?.(e);
        },
        onBlur: (e) => {
          safeTogglePopover(popoverRef.current, 'hide');
          handlers?.onBlur?.(e);
        },
      }
    : { ...handlers };

  return { anchorName, anchorStyle, popoverRef, anchorRef, anchorElement, hoverHandlers };
}

// ============ CONTENT COMPONENT ============
/** Renders tooltip using popover API for top-layer rendering (escapes overflow/transform clipping). */
export const TooltipContent = ({
  anchorName,
  variant = 'gray',
  children,
  popoverRef,
  maxWidth,
  position,
  anchorElement,
  tooltipClass,
  fallbacks,
}: {
  anchorName: string | undefined;
  variant?: ColorVariant;
  children: ReactNode;
  popoverRef: React.RefObject<HTMLSpanElement | null>;
  /** Max width in pixels. When set, tooltip text will wrap. */
  maxWidth?: number;
  /** Position of the tooltip relative to the anchor. If not set, smart positioning is used. */
  position?: TooltipPosition;
  /** Reference to the anchor element for smart positioning calculations. */
  anchorElement?: Element | null;
  /** Additional class name for the tooltip content. */
  tooltipClass?: string;
  /** Overrides the CSS `position-try-fallbacks` chain for this tooltip only. */
  fallbacks?: string;
}) => {
  const { containerLeftX } = useTooltipBounds();

  if (!anchorName) return null;

  const smartPosition = getSmartPosition(anchorElement ?? null, containerLeftX, position);

  return (
    <span
      ref={popoverRef}
      popover="manual"
      role="tooltip"
      style={{ ...tooltipStyle(anchorName, smartPosition, fallbacks), maxWidth }}
      className={cn(tooltipClassName(variant, !!maxWidth), tooltipClass)}>
      {children}
    </span>
  );
};

// ============ POLYMORPHIC COMPONENT ============
type TooltipOwnProps<T extends ElementType> = {
  as?: T;
  tooltip?: ReactNode;
  variant?: ColorVariant;
  /** Color axis. When set, takes precedence over the internal `variant` plumbing prop. */
  color?: ColorName;
  /** Style axis. Defaults to `'filled'` when `color` is set. */
  tone?: Style;
  /** Max width for tooltip in pixels. When set, tooltip text will wrap. */
  tooltipMaxWidth?: number;
  /** Position of the tooltip relative to the anchor. */
  tooltipPosition?: TooltipPosition;
  /** Additional class name for the tooltip content (useful for custom backgrounds). */
  tooltipClassName?: string;
  /**
   * Overrides the CSS `position-try-fallbacks` chain. Use to control the order
   * in which the browser tries alternate positions when the preferred position
   * doesn't fit (e.g. `"top, bottom, flip-inline"` to prefer vertical fallback
   * over horizontal flip for a `tooltipPosition="left"` tooltip).
   */
  tooltipFallbacks?: string;
};

export type TooltipProps<T extends ElementType> = TooltipOwnProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof TooltipOwnProps<T>>;

export default function Tooltip<T extends ElementType = 'span'>({
  as,
  tooltip,
  variant = 'gray',
  color,
  tone,
  tooltipMaxWidth,
  tooltipPosition,
  tooltipClassName: tooltipClass,
  tooltipFallbacks,
  className,
  style,
  children,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ...props
}: TooltipProps<T> & {
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
}) {
  const { anchorName, anchorStyle, popoverRef, anchorRef, anchorElement, hoverHandlers } = useTooltipAnchor(tooltip, {
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
  });
  const Component = as ?? 'span';
  const effectiveVariant: ColorVariant = color != null ? composeVariant(color, tone ?? 'filled') : variant;
  // Merge anchor names if both exist (CSS supports comma-separated anchor-name values)
  // Type cast needed for CSS anchor positioning API (not yet in CSSProperties)
  const passedAnchorName = (style as CSSProperties & { anchorName?: string })?.anchorName;
  const tooltipAnchorName = (anchorStyle as CSSProperties & { anchorName?: string })?.anchorName;
  const mergedAnchorName =
    passedAnchorName && tooltipAnchorName
      ? `${passedAnchorName}, ${tooltipAnchorName}`
      : tooltipAnchorName ?? passedAnchorName;
  const mergedStyle = anchorStyle
    ? { ...style, ...anchorStyle, anchorName: mergedAnchorName }
    : style;

  return (
    <Component
      ref={anchorRef}
      className={className}
      style={mergedStyle}
      {...hoverHandlers}
      {...props}>
      {children}
      <TooltipContent anchorName={anchorName} variant={effectiveVariant} popoverRef={popoverRef} maxWidth={tooltipMaxWidth} position={tooltipPosition} anchorElement={anchorElement} tooltipClass={tooltipClass} fallbacks={tooltipFallbacks}>
        {tooltip}
      </TooltipContent>
    </Component>
  );
}
